import React, { useState } from 'react';
import { useLiveData } from '../hooks/useLiveData';
import { getStudioData, isConfigured } from '../lib/api';
import { studioPanel as mockStudio } from '../data/mockData';

export default function Studio() {
  const configured = isConfigured();
  const [copied, setCopied] = useState(false);

  const { data: script } = useLiveData(
    configured ? getStudioContent : () => Promise.resolve({ body: mockStudio.script, trend_score: mockStudio.trendScore, generated_at: new Date().toISOString(), status: 'draft' }),
    300000 // refresh every 5 mins
  );

  const { data: backlog } = useLiveData(
    configured ? getContentBacklog : () => Promise.resolve([]),
    300000
  );

  const { data: competitors } = useLiveData(
    configured ? getCompetitorScans : () => Promise.resolve([
      { competitor_name: 'Medify', current_price: '£49/mo', price_changed: false, scanned_at: new Date().toISOString() },
      { competitor_name: 'UCAT Ninja', current_price: '£39/mo', price_changed: false, scanned_at: new Date().toISOString() },
    ]),
    300000
  );

  const copy = () => {
    navigator.clipboard.writeText(script?.body || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Studio Panel</div>
          <div className="page-subtitle">Daily content generated by the Marketing Scout at 8:00 AM</div>
        </div>
        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
          {!configured && <span style={{fontSize:'11px',color:'var(--text-3)',background:'var(--bg-2)',padding:'4px 10px',borderRadius:'20px',border:'1px solid var(--border)'}}>demo mode</span>}
          <div style={{fontSize:'10px',color:'var(--text-3)'}}>Trend Score</div>
          <div className="trend-score">{script?.trend_score || '—'}</div>
          <div style={{fontSize:'10px',color:'var(--text-3)'}}>/100</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 280px',gap:'20px',alignItems:'start'}}>
        <div>
          <div className="studio-card" style={{marginBottom:'16px'}}>
            <div className="studio-header">
              <div>
                <div style={{fontSize:'10px',letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--accent-mmf)',marginBottom:'3px'}}>
                  Today's Reel Script
                  {script?.generated_at && <span style={{color:'var(--text-3)',marginLeft:'8px'}}>
                    · {new Date(script.generated_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
                  </span>}
                </div>
                <div style={{fontFamily:'var(--font-display)',fontSize:'15px',fontWeight:'700',color:'var(--text)'}}>MMF — UCAT Content</div>
              </div>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="btn btn-edit" onClick={copy}>{copied ? '✓ Copied' : '📋 Copy Script'}</button>
                <button className="btn btn-approve">✓ Mark Filmed</button>
              </div>
            </div>
            <div className="studio-body">
              {script?.body || 'No script yet. The Marketing Scout runs at 8:00 AM daily. Run it from GitHub Actions to generate today\'s script.'}
            </div>
          </div>

          <div className="section-label">Content Backlog</div>
          {(backlog && backlog.length > 0) ? (
            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
              {backlog.map((item, i) => (
                <div key={i} style={{background:'var(--bg-1)',border:'1px solid var(--border)',borderRadius:'8px',padding:'12px 16px',display:'flex',alignItems:'center',gap:'12px'}}>
                  <div style={{fontSize:'10px',color:'var(--text-3)',minWidth:'50px'}}>
                    {new Date(item.generated_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}
                  </div>
                  <span className={`platform-tag ${item.platform || 'mmf'}`}>{item.platform || 'mmf'}</span>
                  <div style={{fontSize:'12px',color:'var(--text-2)',flex:1}}>{item.title}</div>
                  <span style={{fontSize:'10px',padding:'2px 8px',borderRadius:'4px',background:item.status==='posted'?'rgba(34,197,94,0.1)':'rgba(255,209,102,0.1)',color:item.status==='posted'?'var(--green)':'var(--accent-gold)'}}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="card" style={{color:'var(--text-3)',fontSize:'12px',textAlign:'center',padding:'24px'}}>
              No content history yet. Scripts will appear here after the Marketing Scout runs.
            </div>
          )}
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
          <div className="card">
            <div className="section-label">Competitor Scan</div>
            {(competitors && competitors.length > 0) ? competitors.map((c, i) => (
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid var(--border)',fontSize:'11px'}}>
                <span style={{color:'var(--text-2)',fontWeight:'500'}}>{c.competitor_name}</span>
                <span style={{color:'var(--text-3)'}}>{c.current_price}</span>
                {c.price_changed
                  ? <span style={{fontSize:'9px',color:'var(--red)',fontWeight:'600'}}>⚠ CHANGED</span>
                  : <span style={{fontSize:'9px',color:'var(--green)'}}>✓</span>
                }
              </div>
            )) : (
              <div style={{fontSize:'12px',color:'var(--text-3)',padding:'12px 0'}}>Run the Marketing Scout agent to populate competitor data.</div>
            )}
            {competitors && competitors[0]?.scanned_at && (
              <div style={{fontSize:'10px',color:'var(--text-3)',marginTop:'8px'}}>
                Last scan: {new Date(competitors[0].scanned_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
              </div>
            )}
          </div>

          <div className="card">
            <div className="section-label">Brand Alchemist</div>
            <div style={{fontSize:'12px',color:'var(--text-3)',marginBottom:'12px'}}>Upload a webinar recording to auto-generate 5 TikTok clips + 1 SEO blog post.</div>
            <div style={{border:'2px dashed var(--border)',borderRadius:'8px',padding:'20px',textAlign:'center',cursor:'pointer'}}
              onMouseEnter={e=>e.currentTarget.style.borderColor='var(--border-bright)'}
              onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{fontSize:'20px',marginBottom:'6px'}}>🎬</div>
              <div style={{fontSize:'11px',color:'var(--text-3)'}}>Drop video here</div>
              <div style={{fontSize:'10px',color:'var(--text-3)',marginTop:'2px'}}>MP4, MOV up to 2GB</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
